import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl } from 'react-intl'
import { FilterTable } from 'gnos-tables'
import _ from 'lodash'
import moment from 'moment'

import config from '../../../../../../../../../../../config'

import { createFilterTableId } from '../../../../../../../../helpers'

import { LOCALE_PREFIX } from './constants'

import OwnershipListDetail from './components/OwnershipListDetail'

import hocDialog from '../../../../../../../../../../../services/dialog/hocDialog'
import hocConnect from './hocConnect'

import './style.scss'

@injectIntl
@hocConnect
@hocDialog
export default class OwnershipTab extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        customData: PropTypes.object,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object.isRequired,
        loadDataTable: PropTypes.func.isRequired,
        loadDetailDataTable: PropTypes.func.isRequired,
        locked: PropTypes.bool,
        removeDetailDataTable: PropTypes.func.isRequired,
        saveCustomData: PropTypes.func.isRequired,
    }

    static defaultProps = {
        locked: true,
    }

    componentDidMount() {
        const { id, customData, loadDataTable, locked } = this.props

        const ownershipListLoaded = _.get(customData, 'isLoaded', false)
        if (!ownershipListLoaded && !locked) {
            loadDataTable(id, 0, 10, [
                { id: 'importantRestrictions', desc: true },
                { id: 'cadastralArea', desc: false },
            ])
        }
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.id !== nextProps.id) return true
        if (this.props.block !== nextProps.block) return true
        if (this.props.locked !== nextProps.locked) return true
        if (!_.isEqual(this.props.customData, nextProps.customData)) return true

        return false
    }

    // HANDLERS

    onBannerClick = () => {
        window.open('https://www.bisnode.cz/cz/data-o-nemovitostech/', '_blank')
    }

    onDataTableChange = (tableState) => {
        const { id, loadDataTable } = this.props

        loadDataTable(id, tableState.pagination.page, tableState.pagination.rowsPerPage, tableState.sorted)
    }

    onRowExpanded = (expandedData) => {
        const { id, loadDetailDataTable, removeDetailDataTable } = this.props

        const expand = _.get(expandedData, 'expand', false)
        const listId = _.get(expandedData, 'rowId', null)

        if (expand) loadDetailDataTable(id, listId, 0, 10, [{ id: 'restrictions', desc: true }])
        else removeDetailDataTable(id, listId)
    }

    // RENDER TABLE CELLS

    renderOwnershipListIdCell = (id) => <div className={'ownership-list-number-cell'}>{id}</div>

    renderCadastralAreaCell = (name, id) => {
        const { intl } = this.props

        return (
            <div className={'cadastral-area-cell'}>
                {`${name} (${intl.formatMessage({ id: `${LOCALE_PREFIX}.ownershipList.numberShortcut` })} ${id})`}
            </div>
        )
    }

    renderParticipationCell = (percentage, date) => {
        const dateValue = _.get(date, 'value', null)

        return (
            <div>
                {`${_.round(percentage, 2)} % ${dateValue ? `(${moment(dateValue).format('DD. MM. YYYY')})` : ''}`}
            </div>
        )
    }

    renderOtherOwnersCell = (percentage, count) => {
        if (percentage === 0) return <div>{'-'}</div>

        return <div>{`${_.round(percentage, 2)} % (${count})`}</div>
    }

    renderRestrictionsCell = (count) => {
        const { intl } = this.props

        if (count === null) return <div>{'-'}</div>

        const text = intl.formatMessage(
            { id: `${LOCALE_PREFIX}.ownershipList.restrictions.${count > 0 ? 'yes' : 'no'}` },
            { count }
        )

        return <div>{_.toUpper(text)}</div>
    }

    // RENDER OWNERSHIP DETAIL

    renderOwnershipListDetail = (subComponentData) => {
        const { id, block, saveCustomData } = this.props

        const rowData = _.get(subComponentData, 'row.original', {})
        const listId = _.toString(_.get(rowData, subComponentData.itemIdentifier, null))
        const village = _.toString(_.get(rowData, 'villageName', null))
        const district = _.get(rowData, 'district', null)
        const region = _.toString(_.get(rowData, 'regionName', null))
        const legalRelationships = _.get(rowData, 'legalRelationships', [])

        return (
            <OwnershipListDetail
                id={id}
                block={block}
                listId={listId}
                village={village}
                district={district}
                region={region}
                legalRelationships={legalRelationships}
                saveCustomData={saveCustomData}
            />
        )
    }

    // RENDER MAIN CONTENT

    renderContent = () => {
        const { id, block, customData, intl } = this.props

        const table = _.get(customData, 'table', {})

        const tableData = _.get(table, 'data', [])
        const tableSortAvailableColumns = _.get(table, 'sort.availableColumns', [])
        const tableSortSorted = _.get(table, 'sort.sorted', [])
        const tablePagination = _.get(table, 'pagination', {})

        const tableIsLoading = _.get(customData, 'isLoading', false)

        const restrictionsCount = _.get(customData, 'estatesWithImportantRestrictions', 0)

        return (
            <div className={'ownership-content'}>
                <FilterTable
                    id={createFilterTableId(config.modules.report.key, id, block)}
                    itemIdentifier={'ownershipListId'}
                    loading={tableIsLoading}
                    data={tableData}
                    settings={{
                        sorted: tableSortSorted,
                        pagination: tablePagination,
                    }}
                    columnsDefinition={[
                        {
                            accessor: 'ownershipListNumber',
                            Header: intl.formatMessage({
                                id: `${LOCALE_PREFIX}.ownershipList.header.ownershipListNumber`,
                            }),
                            Cell: ({ value }) => this.renderOwnershipListIdCell(value),
                            sortable: _.includes(tableSortAvailableColumns, 'ownershipListNumber'),
                            width: 120,
                        },
                        {
                            accessor: 'cadastralArea',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.ownershipList.header.cadastralArea` }),
                            Cell: ({ value }) => this.renderCadastralAreaCell(value.name, value.id),
                            sortable: _.includes(tableSortAvailableColumns, 'cadastralArea'),
                            width: 200,
                        },
                        {
                            accessor: 'objectCount',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.ownershipList.header.objectCount` }),
                            sortable: _.includes(tableSortAvailableColumns, 'objectCount'),
                        },
                        {
                            accessor: 'participation',
                            id: 'participationPercentage',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.ownershipList.header.participation` }),
                            Cell: ({ value }) => this.renderParticipationCell(value.percentage, value.startDate),
                            sortable: _.includes(tableSortAvailableColumns, 'participationPercentage'),
                        },
                        {
                            accessor: 'otherOwners',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.ownershipList.header.otherOwners` }),
                            Cell: ({ value }) => this.renderOtherOwnersCell(value.percentage, value.count),
                            sortable: _.includes(tableSortAvailableColumns, 'otherOwners'),
                        },
                        {
                            accessor: 'importantRestrictions',
                            Header: intl.formatMessage(
                                { id: `${LOCALE_PREFIX}.ownershipList.header.restrictions` },
                                { count: restrictionsCount }
                            ),
                            Cell: ({ value }) => this.renderRestrictionsCell(value),
                            sortable: _.includes(tableSortAvailableColumns, 'importantRestrictions'),
                        },
                    ]}
                    SubComponent={this.renderOwnershipListDetail}
                    onTableChange={this.onDataTableChange}
                    onExpandedChange={this.onRowExpanded}
                    texts={{
                        pagination: {
                            page: intl.formatMessage({ id: 'global.page' }),
                            from: intl.formatMessage({ id: 'global.of' }),
                            rowCount: intl.formatMessage({ id: 'global.rowCount' }),
                            rowShow: intl.formatMessage({ id: 'global.rowShow' }),
                            all: intl.formatMessage({ id: 'global.all' }),
                        },
                    }}
                    expandOnRowClick
                />
            </div>
        )
    }

    renderBanner = () => <div className={'real-estates-line-banner'} onClick={this.onBannerClick} />

    render() {
        const { locked } = this.props

        return <div className={'ownership-tab'}>{locked ? this.renderBanner() : this.renderContent()}</div>
    }
}
